import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function ContemporaryFinance55() {
  const contentRef = useRef(null);
  const titleText = "Fiat Money and its Impact on Food";
  const links = {
    prev: "/contemporaryfinance-54",
    index: "/index-finance",
    next: "/contemporaryfinance-56",
  };
  const questions = [
    {
      text: "1. What was one of the major consequences of Richard Nixon abandoning the gold standard in 1971?",
      options: [
        {
          value: "A",
          label: "A. Food became more nutritious",
        },
        {
          value: "B",
          label: "B. Inflation rose and food prices increased",
        },
        {
          value: "C",
          label: "C.  Governments reduced intervention in agriculture",
        },
        {
          value: "D",
          label: "D. People consumed more organic food",
        },
      ],
      correct: "B",
    },
    {
      text: "2. How did governments attempt to lower food prices during the fiat money era?",
      options: [
        { value: "A", label: "A. By promoting organic farming" },
        { value: "B", label: "B. By reversing inflationary policies" },
        {
          value: "C",
          label:
            "C.  By offering loans at low interest rates with the intent to boost production",
        },
        { value: "D", label: "D. By banning artificial fertilizers" },
      ],
      correct: "C",
    },
    {
      text: "3. Why does the Consumer Price Index (CPI) often fail to reflect the true impact of inflation on food?",
      options: [
        { value: "A", label: "A. Because it ignores luxury goods" },
        {
          value: "B",
          label:
            "B. Because people shift from nutritious to cheaper, nutrition-deficient foods due to inflation",
        },
        { value: "C", label: "C. Because it only measures prices in gold" },
        {
          value: "D",
          label: "D. Because food consumption is excluded from CPI",
        },
      ],
      correct: "B",
    },
    {
      text: "4. What has been one of the most significant dietary shifts in the fiat money era?",
      options: [
        {
          value: "A",
          label: "A. Increased consumption of fresh fruits and vegetables",
        },
        {
          value: "B",
          label:
            "B. Widespread reliance on industrially processed “vegetable oils”",
        },
        {
          value: "C",
          label: "C. Return to traditional diets high in animal fats",
        },
        { value: "D", label: "D. Decline in synthetic and lab-made foods" },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={55}
        timerMinutes={60}
        currentLevel={4}
        unlockedBadges={7}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 55</b>! In our journey so far, we have seen how fiat
        money distorts economies, reshapes societies, and alters the very
        incentives that drive human behavior. Today, we turn to a dimension of
        life that is deeply personal and universal i.e our food. Money is one
        half of every transaction, and so the way money is created, managed, and
        devalued inevitably shapes the way food is produced, priced, and
        consumed. When President Richard Nixon severed the final link between
        the dollar and gold in 1971, it was not merely an economic event; it was
        a turning point that set in motion a chain of consequences that reached
        into farms, kitchens, and dining tables across the world.
        <br />
        <br />
        <b>From the Gold Standard to Fiat Interventions in Food</b>
        <br />
        <br />
        To understand how fiat money has shaped modern diets, we must begin with
        the historical moment in 1971 when President Richard Nixon officially
        abandoned the gold standard. Prior to this, the U.S. dollar and other
        major currencies were tethered to gold reserves, which placed natural
        limits on monetary expansion. When that tether was cut, money could be
        printed without being backed by gold, unleashing a new era of rapid
        inflation. Inflation did not exist only in the abstract realm of
        macroeconomic charts. It touched households directly by driving up the
        costs of everyday essentials, with food prices being one of the most
        visible. In a world where money lost value every year, governments and
        central planners devised new interventions in the food sector to offset
        rising costs. One method involved the distribution of loans at
        artificially low interest rates to encourage farmers to scale
        production. Superficially, this might have appeared beneficial, since
        greater supply can temporarily reduce prices. But the hidden costs came
        in the form of soil degradation, widespread monocropping, and the heavy
        reliance on synthetic fertilizers. Farmers who were incentivized to
        maximize yields often did so at the expense of soil health and
        biodiversity. Instead of producing nutrient-dense crops in rotation, the
        system rewarded quantity over quality. The very structure of the fiat
        era thus gave birth to an agricultural model that looked efficient on
        paper but undermined the nutritional foundations of the food supply.
        What began as an economic shift away from sound money soon cascaded into
        a dietary transformation, reducing the quality of what people ate on a
        daily basis.
        <br />
        <br />
        <b>Inflation, Consumer Choices, and the Hidden Cost of Cheap Food</b>
        <br />
        <br />
        Once the supply side was reorganized under fiat-driven policies, the
        demand side also underwent a fundamental change. As inflation persisted,
        the prices of nutrient-rich organic food rose disproportionately
        compared to mass-produced, inorganic alternatives. Families trying to
        stretch their budgets often substituted cheaper food items for
        traditional organic staples. This substitution had profound
        implications. On the surface, governments claimed success because
        consumer price indexes recorded inflation at politically convenient
        levels of two to three percent per year. But beneath the surface, what
        was being measured was not the stable affordability of the same basket
        of goods, but rather the replacement of high-quality items with inferior
        ones. Instead of inflation registering as a sharp increase in household
        food spending, the effect was concealed by the forced decline in
        quality. A household that once purchased fresh fruits, vegetables, and
        meats might now rely on processed grains, industrial oils, and
        subsidized corn-based products. This form of substitution camouflaged
        the true erosion of purchasing power. Government advisors and central
        planners compounded the problem by promoting cheap, nutrient-deficient
        foods as acceptable alternatives. Under the rhetoric of affordability
        and efficiency, society was being nudged toward a diet that not only
        masked inflationary pressures but also undermined long-term health
        outcomes. In this way, inflation was not simply an abstract economic
        inconvenience. It became an active force reshaping food culture, eroding
        traditional diets, and normalizing the consumption of products that were
        once considered low-grade or even unfit for human health.
        <br />
        <br />
        <b>Central Planning, Fiat Money, and the Politics of Food</b>
        <br />
        <br />
        The deeper one looks into the fiat system, the clearer the link between
        monetary expansion and dietary distortion becomes. Under a gold
        standard, if government interventions in the food market failed, they
        would be forced to retract them because resources were finite. Poorly
        designed subsidies or failed agricultural policies could not be
        indefinitely sustained, as governments would eventually run out of gold
        reserves. But in the fiat era, there is no such limitation. With the
        ability to create money at will, central planners can continue promoting
        and funding policies regardless of their real-world effectiveness. This
        shift in constraints gave governments unprecedented power over what
        people eat. Policy makers, influenced by their economic advisors, began
        deciding which foods to promote, which to subsidize, and which to
        discourage. The decision was less about cultural knowledge, ancestral
        dietary wisdom, or community health, and more about what could be
        maintained politically within an unlimited monetary framework. This
        translated into systematic efforts to reshape food culture according to
        bureaucratic visions. Nutrient-rich traditional foods were often deemed
        too expensive, while cheaper processed alternatives were promoted as
        sufficient for the masses. This dynamic reveals a crucial relationship:
        fiat money not only erodes the quality of goods through inflation but
        also empowers governments to impose dietary agendas on populations. Food
        choices, which once emerged organically from centuries of cultural
        practice, now became subject to central planning, backed by the
        seemingly endless stream of fiat currency. Instead of empowering
        individuals to decide for themselves what constitutes health, the fiat
        system allowed authorities to dictate what is affordable, accessible,
        and even socially acceptable to eat.
        <br />
        <br />
        <b>Social Agendas, Synthetic Foods, and the Decline of Nutrition</b>
        <br />
        <br />
        The consequences of this fiat-driven food system extend far beyond
        economics. When fiat inflation made healthy, nutrient-dense foods more
        costly, and when central planning empowered governments to interfere in
        dietary choices, the door was opened for social groups and political
        factions to exploit the system. In democratic societies where votes hold
        political value, groups with strong ideological agendas began lobbying
        governments to reshape food policies according to their preferences.
        Movements advocating for the boycotting of meat or the promotion of
        lab-made synthetic foods gained traction, not purely because of consumer
        demand, but because these agendas aligned with political incentives.
        Politicians, eager to secure votes or funding, found it advantageous to
        amplify such causes. As a result, public policy started to reflect
        ideological visions rather than scientific consensus or cultural
        experience. The fiat system allowed these agendas to flourish because
        there were no hard budgetary constraints to limit the scope of
        intervention. What emerged was a transformation of the food landscape
        itself.
        <br />
        A century ago, the bulk of human fat consumption came from natural
        animal sources such as butter, lard, and tallow, which had nourished
        civilizations for generations. These foods were dense in vitamins,
        minerals, and bioavailable nutrients, providing the kind of sustenance
        that supported strong physical health. Today, in stark contrast, most
        dietary fats are derived from heavily processed industrial products
        misleadingly marketed as “vegetable oils.” These oils are not true
        vegetables in any sense of the term, nor do they resemble the
        traditional fats they replaced. Instead, they are chemically altered
        substances extracted from soy, corn, canola, and other mass-produced
        crops, which are heavily subsidized under fiat-era agricultural
        policies. The marketing of these oils as healthy alternatives was not a
        spontaneous consumer-led movement but the result of decades of central
        planning, government subsidies, and corporate influence—all made
        possible by the endless funding stream of fiat currency.
        <br />
        Without the distortions created by fiat money, it is inconceivable that
        such a drastic shift in dietary norms would have occurred. In a system
        bound by gold or other hard constraints, governments could not endlessly
        subsidize industrial agriculture or promote synthetic substitutes over
        natural foods. Yet under fiat, political agendas, corporate lobbying,
        and ideological campaigns all converge to redefine what people eat,
        regardless of long-term health consequences. The result is a society in
        which cheap, highly processed foods dominate the average diet, while
        genuinely nutritious foods are priced out of reach for many. The decline
        in nutrition, rising rates of obesity, metabolic disease, and chronic
        illness are not random outcomes. They are the predictable legacy of a
        fiat money system that erodes purchasing power, distorts markets,
        empowers central planners, and ultimately reshapes culture itself. The
        story of modern food is inseparable from the story of fiat inflation,
        because the very money people use every day is one half of every
        transaction, and through its distortions, it has reshaped not only
        economies but also the health of entire civilizations.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br /> Today, we learned how fiat money, by fueling inflation and
        enabling endless government intervention, has reshaped the very
        foundation of our food supply. Once tied to natural cycles and cultural
        traditions, food production has been redirected toward cheap,
        mass-produced, and nutrient-deficient alternatives designed to mask the
        rising cost of living. The abandonment of the gold standard in 1971 not
        only unleashed inflation but also gave governments the unchecked ability
        to promote policies that favor industrial farming and processed
        substitutes over wholesome, organic food. As a result, society has
        gradually shifted from diets rich in natural animal fats and traditional
        produce to diets dominated by synthetic, government-endorsed “vegetable
        oils” and factory-made products. In the fiat era, money’s distortion of
        incentives is visible not only in weakened economies and fragile
        societies but also in the very food people eat, undermining health,
        culture, and long-term well-being.
        <br />
        <br /> <b> Coming Up Next</b>
        <br />
        <br />
        In the following lesson, we will shift our focus from the body to the
        mind and examine how fiat money shapes education. When central planners
        gain control over the flow of funds, schools and universities no longer
        serve the aspirations of students or the values of families, but rather
        the political goals of those who print the money. From public schools
        that remain immune to accountability, to universities that are sustained
        by subsidies and loans, the entire education system becomes distorted.
        Day 56 will uncover how this reliance on fiat financing weakens
        intellectual development, fosters bureaucratic expansion, and ultimately
        undermines the very purpose of learning.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}
      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default ContemporaryFinance55;
